import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LifeBuoy, Plus, MessageSquare, Star, RefreshCw, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import MainLayout from '../components/MainLayout';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  open: 'bg-slate-100 text-slate-700',
  assigned: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  waiting_on_employee: 'bg-purple-100 text-purple-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-gray-200 text-gray-700',
  reopened: 'bg-rose-100 text-rose-700',
};

const PRIORITY_BADGE = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

const slaCountdown = (deadline) => {
  if (!deadline) return null;
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms < 0) return { overdue: true, label: `Overdue ${Math.ceil(-ms / 3600000)}h` };
  const hrs = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  return { overdue: false, label: `${hrs}h ${mins}m left` };
};

export default function HelpDesk() {
  const [meta, setMeta] = useState({ categories: [], priorities: [] });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');

  const [form, setForm] = useState({
    subject: '', description: '', category: 'payroll',
    sub_category: '', priority: 'medium', related_month: '',
  });

  const loadMeta = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/helpdesk/categories`, { withCredentials: true });
      setMeta(data);
    } catch { /* swallow */ }
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/helpdesk/tickets?scope=mine`, { withCredentials: true });
      setTickets(data.tickets || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMeta(); loadTickets(); }, []);

  const submit = async () => {
    if (!form.subject.trim() || form.subject.trim().length < 3) {
      toast.error('Subject must be at least 3 characters'); return;
    }
    if (!form.description.trim() || form.description.trim().length < 3) {
      toast.error('Please describe your issue'); return;
    }
    try {
      await axios.post(`${API_URL}/helpdesk/tickets`, form, { withCredentials: true });
      toast.success('Ticket raised — we will get back to you soon');
      setShowCreate(false);
      setForm({ subject: '', description: '', category: 'payroll', sub_category: '', priority: 'medium', related_month: '' });
      loadTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to raise ticket');
    }
  };

  const visibleTickets = useMemo(() => {
    if (filter === 'all') return tickets;
    if (filter === 'open') return tickets.filter(t => !['resolved', 'closed'].includes(t.status));
    if (filter === 'resolved') return tickets.filter(t => ['resolved', 'closed'].includes(t.status));
    return tickets;
  }, [tickets, filter]);

  const subOptions = useMemo(() => {
    const cat = meta.categories?.find(c => c.key === form.category);
    return cat?.sub_categories || [];
  }, [form.category, meta.categories]);

  return (
    <MainLayout>
      <div className="space-y-6 max-w-7xl" data-testid="helpdesk-ess-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <LifeBuoy className="text-amber-600" size={28} /> Help Desk
            </h1>
            <p className="text-gray-600 mt-1">Raise a ticket for payroll, attendance, HR or system issues.</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="raise-ticket-btn"
            className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-5 py-2.5 rounded-lg font-medium hover:opacity-90 flex items-center gap-2 shadow"
          >
            <Plus size={18} /> Raise Ticket
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          {['all', 'open', 'resolved'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium ${filter === f ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`}>
              {f === 'all' ? 'All' : f === 'open' ? 'Open' : 'Resolved'}
            </button>
          ))}
          <button onClick={loadTickets} className="ml-auto text-slate-600 hover:text-slate-900 flex items-center gap-1 text-sm">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading && <div className="p-6 text-center text-gray-500">Loading…</div>}
          {!loading && visibleTickets.length === 0 && (
            <div className="p-10 text-center text-gray-500">
              <LifeBuoy size={36} className="mx-auto text-slate-300 mb-3" />
              No tickets yet. Click <b>Raise Ticket</b> to report an issue.
            </div>
          )}
          {!loading && visibleTickets.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Ticket #</th>
                  <th className="text-left px-4 py-3 font-medium">Subject</th>
                  <th className="text-left px-4 py-3 font-medium">Category</th>
                  <th className="text-left px-4 py-3 font-medium">Priority</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-left px-4 py-3 font-medium">SLA</th>
                  <th className="text-left px-4 py-3 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {visibleTickets.map(t => {
                  const sla = slaCountdown(t.sla_deadline);
                  return (
                    <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                        onClick={() => setSelected(t)} data-testid={`ess-ticket-row-${t.ticket_number}`}>
                      <td className="px-4 py-3 font-mono text-blue-700">{t.ticket_number}</td>
                      <td className="px-4 py-3 font-medium">{t.subject}</td>
                      <td className="px-4 py-3 capitalize">{t.category.replace('_', ' ')}{t.sub_category ? ` · ${t.sub_category}` : ''}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${PRIORITY_BADGE[t.priority]}`}>{t.priority}</span></td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[t.status]}`}>{t.status.replace(/_/g, ' ')}</span></td>
                      <td className="px-4 py-3">
                        {['resolved', 'closed'].includes(t.status)
                          ? <span className="text-emerald-700 text-xs flex items-center gap-1"><CheckCircle2 size={12}/> Done</span>
                          : sla?.overdue
                            ? <span className="text-red-700 text-xs flex items-center gap-1"><AlertTriangle size={12}/> {sla.label}</span>
                            : <span className="text-slate-600 text-xs flex items-center gap-1"><Clock size={12}/> {sla?.label}</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-500">{fmtDate(t.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {showCreate && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-6" onClick={e => e.stopPropagation()} data-testid="raise-ticket-modal">
              <h2 className="text-xl font-bold mb-4">Raise a New Ticket</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Subject *</label>
                  <input autoFocus value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                    data-testid="ticket-subject"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 mt-1" placeholder="Short summary of the issue" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Category *</label>
                    <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value, sub_category: '' })}
                      data-testid="ticket-category"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 mt-1">
                      {meta.categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Sub-category</label>
                    <select value={form.sub_category} onChange={e => setForm({ ...form, sub_category: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 mt-1">
                      <option value="">— select —</option>
                      {subOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Priority *</label>
                    <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                      data-testid="ticket-priority"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 mt-1">
                      {(meta.priorities || ['low', 'medium', 'high', 'urgent']).map(p =>
                        <option key={p} value={p}>{`${p.toUpperCase()} — SLA ${meta.sla_hours?.[p] || ''}h`}</option>)}
                    </select>
                  </div>
                  {(form.category === 'payroll' || form.category === 'attendance') && (
                    <div>
                      <label className="text-sm font-medium text-slate-700">Related Month (optional)</label>
                      <input type="month" value={form.related_month}
                        onChange={e => setForm({ ...form, related_month: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 mt-1" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Description *</label>
                  <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                    data-testid="ticket-description"
                    rows={5} className="w-full border border-slate-300 rounded-lg px-3 py-2 mt-1"
                    placeholder="Describe the issue with as much detail as possible — month, amount, error message, etc." />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700">Cancel</button>
                <button onClick={submit} data-testid="submit-ticket-btn"
                  className="px-5 py-2 rounded-lg bg-amber-600 text-white font-medium">Submit Ticket</button>
              </div>
            </div>
          </div>
        )}

        {selected && <TicketDetail t={selected} onClose={() => setSelected(null)} onChanged={loadTickets} />}
      </div>
    </MainLayout>
  );
}

function TicketDetail({ t, onClose, onChanged }) {
  const [ticket, setTicket] = useState(t);
  const [reply, setReply] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [showReopen, setShowReopen] = useState(false);
  const [showRate, setShowRate] = useState(false);
  const [rating, setRating] = useState(5);
  const [ratingComment, setRatingComment] = useState('');

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/helpdesk/tickets/${t.id}`, { withCredentials: true });
      setTicket(data);
    } catch { /* */ }
  };
  useEffect(() => { refresh(); }, []);  // eslint-disable-line

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await axios.post(`${API_URL}/helpdesk/tickets/${ticket.id}/comments`,
        { message: reply, is_internal: false }, { withCredentials: true });
      setReply('');
      await refresh();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reply failed'); }
  };

  const reopen = async () => {
    if (!reopenReason.trim()) { toast.error('Please tell us why you are reopening'); return; }
    try {
      await axios.post(`${API_URL}/helpdesk/tickets/${ticket.id}/reopen`,
        { message: reopenReason }, { withCredentials: true });
      toast.success('Ticket reopened');
      setShowReopen(false); setReopenReason('');
      await refresh(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reopen failed'); }
  };

  const rate = async () => {
    try {
      await axios.post(`${API_URL}/helpdesk/tickets/${ticket.id}/rate`,
        { rating, rating_comment: ratingComment }, { withCredentials: true });
      toast.success('Thanks for your feedback');
      setShowRate(false);
      await refresh(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rating failed'); }
  };

  const isResolved = ['resolved', 'closed'].includes(ticket.status);
  const sla = slaCountdown(ticket.sla_deadline);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()} data-testid="ticket-detail-modal">
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-xs font-mono text-blue-700">{ticket.ticket_number}</div>
              <h2 className="text-xl font-bold mt-1">{ticket.subject}</h2>
              <div className="flex gap-2 mt-2 items-center">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[ticket.status]}`}>{ticket.status.replace(/_/g, ' ')}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${PRIORITY_BADGE[ticket.priority]}`}>{ticket.priority}</span>
                <span className="text-xs text-slate-500 capitalize">{ticket.category.replace('_', ' ')}{ticket.sub_category ? ` · ${ticket.sub_category}` : ''}</span>
                {!isResolved && sla && (
                  <span className={`text-xs ${sla.overdue ? 'text-red-700' : 'text-slate-500'}`}>{sla.label}</span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-xs text-slate-500 mb-1">{ticket.raised_by_name} · {fmtDate(ticket.created_at)}</div>
            <div className="text-sm whitespace-pre-wrap">{ticket.description}</div>
          </div>

          {ticket.assigned_to_name && (
            <div className="text-xs text-slate-600">Assigned to <b>{ticket.assigned_to_name}</b> ({ticket.assigned_role})</div>
          )}

          <div className="space-y-3">
            {(ticket.comments || []).map(c => (
              <div key={c.id} className={`rounded-lg p-3 ${c.is_admin ? 'bg-blue-50 border-l-4 border-blue-400' : 'bg-emerald-50 border-l-4 border-emerald-400'}`}>
                <div className="text-xs text-slate-600 mb-1">
                  <b>{c.user_name}</b> {c.is_admin ? '(Support)' : '(You)'} · {fmtDate(c.created_at)}
                </div>
                <div className="text-sm whitespace-pre-wrap">{c.message}</div>
              </div>
            ))}
          </div>

          {!isResolved && (
            <div>
              <textarea rows={3} value={reply} onChange={e => setReply(e.target.value)}
                data-testid="ticket-reply-input"
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
                placeholder="Add a reply or more details…" />
              <div className="flex justify-end mt-2">
                <button onClick={sendReply} disabled={!reply.trim()}
                  data-testid="ticket-reply-btn"
                  className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm disabled:opacity-50 flex items-center gap-1">
                  <MessageSquare size={14} /> Send Reply
                </button>
              </div>
            </div>
          )}

          {isResolved && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="font-medium text-emerald-800 mb-2">This ticket has been {ticket.status}.</div>
              <div className="flex gap-2">
                {!ticket.rating && (
                  <button onClick={() => setShowRate(true)} data-testid="rate-ticket-btn"
                    className="px-3 py-1.5 rounded bg-amber-500 text-white text-sm flex items-center gap-1">
                    <Star size={14} /> Rate Resolution
                  </button>
                )}
                <button onClick={() => setShowReopen(true)} data-testid="reopen-ticket-btn"
                  className="px-3 py-1.5 rounded bg-rose-600 text-white text-sm flex items-center gap-1">
                  <RefreshCw size={14} /> Reopen (within 7 days)
                </button>
              </div>
              {ticket.rating && (
                <div className="text-sm text-slate-700 mt-3">You rated this <b>{ticket.rating}/5</b> {ticket.rating_comment ? `— "${ticket.rating_comment}"` : ''}</div>
              )}
            </div>
          )}

          {showReopen && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
              <div className="font-medium text-rose-800 mb-2">Reopen ticket — what is still wrong?</div>
              <textarea rows={3} value={reopenReason} onChange={e => setReopenReason(e.target.value)}
                data-testid="reopen-reason"
                className="w-full border border-rose-300 rounded-lg px-3 py-2 mb-2" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowReopen(false)} className="px-3 py-1.5 rounded bg-slate-100 text-slate-700 text-sm">Cancel</button>
                <button onClick={reopen} data-testid="confirm-reopen-btn" className="px-4 py-1.5 rounded bg-rose-600 text-white text-sm">Confirm Reopen</button>
              </div>
            </div>
          )}

          {showRate && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="font-medium text-amber-800 mb-2">Rate the resolution</div>
              <div className="flex gap-1 mb-2">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} onClick={() => setRating(n)}
                    className={`p-1.5 ${n <= rating ? 'text-amber-500' : 'text-slate-300'}`}>
                    <Star size={20} fill={n <= rating ? 'currentColor' : 'none'} />
                  </button>
                ))}
              </div>
              <input value={ratingComment} onChange={e => setRatingComment(e.target.value)}
                placeholder="Optional comment"
                className="w-full border border-amber-300 rounded-lg px-3 py-2 mb-2" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowRate(false)} className="px-3 py-1.5 rounded bg-slate-100 text-slate-700 text-sm">Cancel</button>
                <button onClick={rate} data-testid="confirm-rate-btn" className="px-4 py-1.5 rounded bg-amber-600 text-white text-sm">Submit Rating</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
